import { TrendingUp } from "lucide-react";
import useFunnelData from "@/hooks/useFunnelData";

function ConvBar({ rate }) {
  return (
    <div className="flex items-center gap-2">
      <div className="h-2 w-24 bg-slate-100 rounded-sm overflow-hidden">
        <div
          className="h-full bg-emerald-600"
          style={{ width: `${Math.min(100, rate)}%` }}
        />
      </div>
      <span className="mono text-[10px] tracking-[0.18em] uppercase text-emerald-700 w-10 text-right">
        {rate}%
      </span>
    </div>
  );
}

function FunnelHeader({ totals, rangeActive }) {
  return (
    <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
      <div className="mono text-[10px] tracking-[0.22em] uppercase text-emerald-700 flex items-center gap-2">
        <TrendingUp className="h-3 w-3" /> Service funnel · checklist → quote
        {rangeActive && <span className="text-slate-400">· in selected range</span>}
      </div>
      <div className="mono text-[10px] tracking-[0.18em] uppercase text-slate-500">
        {totals.downloads} downloads · {totals.quotes} quotes ·{" "}
        <span className="text-emerald-700">{totals.rate}% conv</span>
      </div>
    </div>
  );
}

function FunnelTable({ rows }) {
  const cols = ["Service slug", "Checklist downloads", "Quote requests", "Converted", "Conv %"];
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left">
        <thead className="bg-paper-dark/40">
          <tr>
            {cols.map((c) => (
              <th
                key={c}
                className="p-3 mono text-[10px] tracking-[0.18em] uppercase text-slate-500"
              >
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.slug}
              data-testid={`funnel-row-${r.slug}`}
              className="border-t border-slate-100"
            >
              <td className="p-3 mono text-xs text-topo">{r.slug}</td>
              <td className="p-3 text-sm font-semibold text-slate-900">{r.downloads}</td>
              <td className="p-3 text-sm text-slate-900">{r.quotes}</td>
              <td className="p-3 text-sm text-emerald-700 font-semibold">{r.converted}</td>
              <td className="p-3">
                <ConvBar rate={r.rate} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EmptyState() {
  return (
    <section
      data-testid="admin-funnel"
      className="mb-10 border border-slate-200 bg-white p-6"
    >
      <div className="mono text-[10px] tracking-[0.22em] uppercase text-emerald-700 flex items-center gap-2">
        <TrendingUp className="h-3 w-3" /> Service funnel · checklist → quote
      </div>
      <p className="mt-2 text-sm text-slate-600">
        No checklist downloads or service quotes yet. Once visitors start unlocking
        checklists, conversion rates will appear here.
      </p>
    </section>
  );
}

/**
 * FunnelPanel — pairs `checklist-{slug}` contacts with `quote-form-{slug}`
 * quotes from the same email, then renders the per-service funnel table.
 * Heavy lifting lives in `useFunnelData` / `computeFunnel`.
 */
export default function FunnelPanel({ contacts, quotes, rangeActive }) {
  const { rows, totals } = useFunnelData(contacts, quotes);

  if (rows.length === 0) return <EmptyState />;

  return (
    <section data-testid="admin-funnel" className="mb-10 border border-slate-200 bg-white">
      <FunnelHeader totals={totals} rangeActive={rangeActive} />
      <FunnelTable rows={rows} />
    </section>
  );
}
